import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@services/http/client';
import { billingApi } from '@services/api/billing';
import { queryKeys } from '@services/api/queryKeys';
import type {
  CheckoutSession,
  Invoice,
  Paginated,
  Plan,
  PortalSession,
  Subscription,
} from '@app-types/index';
import type { CheckoutInput, PortalInput } from '@services/api/billing';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export const usePlans = (): UseQueryResult<Paginated<Plan>, ApiError> =>
  useQuery({
    queryKey: queryKeys.billing.plans(),
    queryFn: () => billingApi.plans(),
    // Plans rarely change — keep them fresh for the whole session.
    staleTime: 5 * 60_000,
  });

export const useMySubscription = (): UseQueryResult<Subscription | null, ApiError> =>
  useQuery({
    queryKey: queryKeys.billing.subscription(),
    queryFn: () => billingApi.mySubscription(),
  });

export const useInvoices = (): UseQueryResult<Invoice[], ApiError> =>
  useQuery({
    queryKey: queryKeys.billing.invoices(),
    queryFn: () => billingApi.invoices(),
  });

// ---------------------------------------------------------------------------
// Mutations — both create a Stripe-hosted session; the caller redirects to
// the returned `url`. They do not auto-navigate so the UI keeps control.
// ---------------------------------------------------------------------------
export const useCheckout = (): UseMutationResult<CheckoutSession, ApiError, CheckoutInput> =>
  useMutation({
    mutationFn: (input: CheckoutInput) => billingApi.checkoutSession(input),
  });

export const useOpenPortal = (): UseMutationResult<PortalSession, ApiError, PortalInput> =>
  useMutation({
    mutationFn: (input: PortalInput) => billingApi.portalSession(input),
  });

// ---------------------------------------------------------------------------
// Subscription-state mutations. Each returns the fresh {subscription} envelope;
// on success we prime the subscription cache and refresh invoices (a plan
// change or cancellation can produce a proration/credit invoice).
// ---------------------------------------------------------------------------
const useSubscriptionMutation = <TArg>(
  mutationFn: (arg: TArg) => Promise<Subscription | null>,
): UseMutationResult<Subscription | null, ApiError, TArg> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (subscription) => {
      queryClient.setQueryData(queryKeys.billing.subscription(), subscription);
      void queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoices() });
    },
  });
};

export const useConfirmCheckout = (): UseMutationResult<Subscription | null, ApiError, string> =>
  useSubscriptionMutation((sessionId: string) => billingApi.confirmCheckout(sessionId));

export const useChangePlan = (): UseMutationResult<Subscription | null, ApiError, string> =>
  useSubscriptionMutation((planId: string) => billingApi.changePlan(planId));

export const useCancelSubscription = (): UseMutationResult<Subscription | null, ApiError, void> =>
  useSubscriptionMutation(() => billingApi.cancel());

export const useResumeSubscription = (): UseMutationResult<Subscription | null, ApiError, void> =>
  useSubscriptionMutation(() => billingApi.resume());
